import { z } from 'zod';

/**
 * Four cumulative tiers (BUILD-735):
 * - `user` — create/manage their own matrices, scoped to exactly one company
 *   (via `company_memberships`, same mechanism as the former `membre_entreprise`).
 * - `admin` — everything `user` can, plus modify their own company's parameters
 *   (info, setup de singularité, paliers). Still scoped to exactly one company.
 * - `pms_user` — everything `admin` can, plus create companies. Global role,
 *   not tied to a single company (unrestricted reach, like the former `pms`).
 * - `pms_admin` — every right, including claude-credentials management and the
 *   composed-prompt reveal. The superset — equivalent to the former `pms` role.
 *
 * See docs/design/matrice-creation-wizard/.
 */
export const userRoleSchema = z.enum(['user', 'admin', 'pms_user', 'pms_admin']);
export type UserRole = z.infer<typeof userRoleSchema>;

export const userSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().min(1).max(200),
  role: userRoleSchema,
  createdAt: z.string().datetime(),
});
export type User = z.infer<typeof userSchema>;

/**
 * The short name the grid shows for a matrix's creator (issue #16): the first
 * word of the display name and the initial of the last one, « Alice Pesty » →
 * « Alice P. ». A single-word name is shown whole.
 */
export function toShortDisplayName(displayName: string): string {
  const words = displayName
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  const first = words[0];
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  if (first === undefined) return '';
  const initial = last === undefined ? undefined : [...last][0];
  return initial === undefined ? first : `${first} ${initial.toUpperCase()}.`;
}
